import { formatNumber } from "../../../utils/FormattingUtils";
import {
  applyTransformations,
  applyTransformationsCached,
} from "../../calculation/Calculation";
import { TransformationTags } from "../../calculation/TransformationTags";
import { Identifiable } from "../../generic/Identifiable";
import { addToInventory } from "../../items/Inventory";
import { getItemById } from "../../items/Items";
import {
  PlayerContextState,
  PlayerContextTransform,
} from "../../PlayerContext";
import { grantResource, Resource } from "../../Resources";
import { AttackTarget } from "../AttackTarget";
import { CombatStat } from "../CombatStats";
import {
  pushToCreatureMessageLog,
  wouldBeThrottledIfPushingToCreatureMessageLog,
} from "../Creatures";
import { pushToMessageLog, standardEnemyAttackEffect } from "../Exploration";
import { Enemies } from "./Enemies";

export interface BattlerStats {
  maxHP: number;
  attack: number;
  attackDelay: number;
  defense: number;
  dodgeChance: number;
  critChance: number;
}

export interface BattlerAction {
  name: string;
  delay: number;
  transform: PlayerContextTransform;
}

export interface EnemyLoot {
  itemId: string;
  chance: number;
  amount: number;
  params?: any;
}

export enum BestiaryState {
  Hidden = "Hidden",
  Basic = "Basic",
  Expanded = "Expanded",
  Loot = "Loot",
}

export function getCumulativeEnemyKills(
  state: PlayerContextState,
  enemyId: string,
): number {
  return (
    getCumulativeEnemyKillsFromCreatures(state, enemyId) +
    (state.global.timesEnemyDefeated?.[enemyId]?.player ?? 0)
  );
}

export function getCumulativeEnemyKillsFromCreatures(
  state: PlayerContextState,
  enemyId: string,
): number {
  return state.global.timesEnemyDefeated?.[enemyId]?.creatures ?? 0;
}

export function getBestiaryVisibleEnemies(state: PlayerContextState): Enemy[] {
  return Enemies.getAll().filter(
    (enemy) => enemy.getBestiaryState(state) != BestiaryState.Hidden,
  );
}

export abstract class Enemy implements Identifiable {
  abstract getId(): string;
  abstract getName(): string;
  abstract getBaseStats(): BattlerStats;
  abstract getPicture(state: PlayerContextState): any;
  abstract getLevel(): number;

  getMaxHP(state: PlayerContextState): number {
    return applyTransformations(
      [AttackTarget.Enemy, CombatStat.MaxHP],
      state,
      this.getBaseStats().maxHP,
    );
  }
  getAttack(state: PlayerContextState): number {
    return applyTransformations(
      [AttackTarget.Enemy, CombatStat.Attack],
      state,
      this.getBaseStats().attack,
    );
  }
  getAttackDelay(state: PlayerContextState): number {
    return this.getBaseStats().attackDelay;
  }
  getCriticalChance(state: PlayerContextState): number {
    return applyTransformations(
      [AttackTarget.Enemy, CombatStat.CritChance],
      state,
      this.getBaseStats().critChance,
    );
  }
  getDefense(state: PlayerContextState): number {
    return applyTransformations(
      [AttackTarget.Enemy, CombatStat.Defense],
      state,
      this.getBaseStats().defense,
    );
  }
  getDodgeChance(state: PlayerContextState): number {
    return applyTransformations(
      [AttackTarget.Enemy, CombatStat.Dodge],
      state,
      this.getBaseStats().dodgeChance,
    );
  }

  getNextAction(state: PlayerContextState): BattlerAction {
    return {
      name: "Attack",
      delay: this.getAttackDelay(state),
      transform: (state) =>
        standardEnemyAttackEffect(
          state,
          1.0,
          this.getAttack(state),
          this.getCriticalChance(state),
        ),
    };
  }

  abstract getCoinsAwarded(state: PlayerContextState): number;

  abstract getMonstiumAwarded(state: PlayerContextState): number;

  getItemsAwarded(state: PlayerContextState): EnemyLoot[] {
    return [];
  }

  isBoss(): boolean {
    return false;
  }

  getBackdropColor(): string | undefined {
    return undefined;
  }

  getBestiaryState(state: PlayerContextState): BestiaryState {
    const kills = getCumulativeEnemyKills(state, this.getId());
    if (kills > (this.isBoss() ? 0 : 49)) {
      return BestiaryState.Loot;
    }
    if (kills > (this.isBoss() ? 0 : 9)) {
      return BestiaryState.Expanded;
    }
    if (kills > 0) {
      return BestiaryState.Basic;
    }
    return BestiaryState.Hidden;
  }

  onDeath(
    state: PlayerContextState,
    isFromCreatures: boolean,
    lootMultiplier?: number,
  ): PlayerContextState {
    lootMultiplier = lootMultiplier ?? 1.0;
    const totalTimesDefeated = state.global.timesEnemyDefeated?.[
      this.getId()
    ] ?? { player: 0, creatures: 0 };
    if (isFromCreatures) {
      totalTimesDefeated.creatures++;
    } else {
      totalTimesDefeated.player++;
    }
    state.global.timesEnemyDefeated[this.getId()] = totalTimesDefeated;

    const baseCoinsAwarded = this.getCoinsAwarded(state);
    const baseMonstiumAwarded = this.getMonstiumAwarded(state);
    const coinsAwarded =
      applyTransformationsCached(
        [
          TransformationTags.Loot,
          Resource.Coins,
          isFromCreatures
            ? TransformationTags.FromCreatures
            : TransformationTags.FromExploration,
        ],
        state,
        baseCoinsAwarded,
      ) * lootMultiplier;
    const monstiumAwarded =
      applyTransformationsCached(
        [
          TransformationTags.Loot,
          Resource.Monstium,
          isFromCreatures
            ? TransformationTags.FromCreatures
            : TransformationTags.FromExploration,
        ],
        state,
        baseMonstiumAwarded,
      ) * lootMultiplier;

    state = grantResource(Resource.Coins, coinsAwarded)(state);
    state = grantResource(Resource.Monstium, monstiumAwarded)(state);
    if (!isFromCreatures) {
      state = pushToMessageLog(
        state,
        `You found ${formatNumber(coinsAwarded)} :coins: and ${formatNumber(
          monstiumAwarded,
        )} :monstium:!`,
      );
    } else {
      if (!wouldBeThrottledIfPushingToCreatureMessageLog(state)) {
        state = pushToCreatureMessageLog(
          state,
          `A creature found ${formatNumber(
            coinsAwarded,
          )} :coins: and ${formatNumber(monstiumAwarded)} :monstium:!`,
        );
      }
    }
    const itemsAwarded = this.getItemsAwarded(state);
    itemsAwarded.forEach((enemyLoot) => {
      let chance = enemyLoot.chance * (lootMultiplier ?? 1.0);
      if (!isFromCreatures) {
        chance = applyTransformations(
          [TransformationTags.LootChance],
          state,
          chance,
        );
      }
      if (Math.random() < chance) {
        const item = getItemById(enemyLoot.itemId);
        const params = enemyLoot.params || {};
        const itemOccurrence = { itemId: enemyLoot.itemId, params };
        const fitsInInventory = item.fitsInInventory(state, params, true);
        const fitsInInventoryText = fitsInInventory
          ? ""
          : ", but your inventory is full!";
        state = addToInventory(itemOccurrence, enemyLoot.amount, state);
        if (!isFromCreatures) {
          state = pushToMessageLog(
            state,
            `You found ${enemyLoot.amount} ${item.getName(
              params,
            )}${fitsInInventoryText}!`,
          );
        } else {
          state = pushToCreatureMessageLog(
            state,
            `A creature found ${enemyLoot.amount} ${item.getName(
              params,
            )}${fitsInInventoryText}!`,
          );
        }
      }
    });
    return state;
  }
}
